import React from "react";
import { X } from "lucide-react";
import type { CabinetNode, CabinetObjectStatus } from "../../types/cabinet";
import { NODE_KIND_STYLES, STATUS_CHIP_CLASSES } from "./cabinetTheme";

interface CabinetNodePanelProps {
	node: CabinetNode;
	onClose: () => void;
}

interface StatusChipProps {
	status: CabinetObjectStatus;
}

const StatusChip: React.FC<StatusChipProps> = ({ status }) => (
	<span
		className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_CHIP_CLASSES[status]}`}
	>
		{status}
	</span>
);

function formatConfidence(confidence: number | null | undefined): string | null {
	if (typeof confidence !== "number" || !Number.isFinite(confidence)) {
		return null;
	}
	return `${Math.round(confidence * 100)}% confidence`;
}

/**
 * Detail panel for the selected graph node.
 *
 * For a document this is the frontmatter the bundle would carry: classification
 * (with the evidence line that justified it), period, canonical status, and the
 * full version chain from `meta.versions` with the current version marked.
 * Deals and entities get the small amount the contract carries for them.
 */
const CabinetNodePanel: React.FC<CabinetNodePanelProps> = ({
	node,
	onClose,
}) => {
	const versions = node.meta.versions ?? [];
	const confidence = formatConfidence(node.confidence);

	return (
		<aside
			aria-label="Node details"
			className="flex h-full w-full flex-col overflow-y-auto border-l border-gray-200 bg-white"
		>
			<div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
				<div>
					<div className="text-xs uppercase tracking-wider text-text-muted">
						{NODE_KIND_STYLES[node.kind].label}
					</div>
					<h2 className="mt-0.5 text-lg font-semibold text-text-strong">
						{node.label}
					</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close details"
					className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
				>
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="space-y-4 px-4 py-4">
				<dl className="space-y-2">
					{node.deal && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">Deal</dt>
							<dd className="text-sm text-text-base">{node.deal}</dd>
						</div>
					)}
					{node.doc_type && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">Type</dt>
							<dd className="text-sm text-text-base">
								<span className="font-mono">{node.doc_type}</span>
								{confidence && (
									<span className="block text-xs text-text-muted">
										{confidence}
									</span>
								)}
							</dd>
						</div>
					)}
					{node.meta.entity_type && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">
								Entity type
							</dt>
							<dd className="font-mono text-sm text-text-base">
								{node.meta.entity_type}
							</dd>
						</div>
					)}
					{(node.meta.deals?.length ?? 0) > 0 && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">
								Appears in
							</dt>
							{/* A canonical entity spans rooms — that is the point of it. */}
							<dd className="text-sm text-text-base">
								{node.meta.deals?.join(", ")}
							</dd>
						</div>
					)}
					{(node.meta.aliases?.length ?? 0) > 1 && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">
								Also written
							</dt>
							{/* Every spelling the classifier read off a document body. The
							    variant is evidence about a document, so it is shown, not
							    normalized away. */}
							<dd className="flex flex-wrap gap-1">
								{node.meta.aliases
									?.filter((alias) => alias !== node.label)
									.map((alias) => (
										<span
											key={alias}
											className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-xs text-text-muted"
										>
											{alias}
										</span>
									))}
							</dd>
						</div>
					)}
					{node.meta.period && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">Period</dt>
							<dd className="text-sm text-text-base">{node.meta.period}</dd>
						</div>
					)}
					{node.status && (
						<div className="flex gap-2">
							<dt className="w-28 shrink-0 text-sm text-text-muted">Status</dt>
							<dd>
								<StatusChip status={node.status} />
							</dd>
						</div>
					)}
				</dl>

				{node.evidence && (
					<div className="rounded-md border border-gray-200 bg-gray-50 p-3">
						<div className="text-xs font-medium text-text-muted">Evidence</div>
						<p className="mt-1 text-sm italic text-text-base">
							“{node.evidence}”
						</p>
					</div>
				)}

				{node.kind === "document" && (
					<section>
						<h3 className="text-base font-medium text-text-base">
							Version chain
						</h3>
						{versions.length === 0 ? (
							<p className="mt-1 text-sm text-text-muted">
								No versions recorded for this document.
							</p>
						) : (
							<ol className="mt-2 space-y-2">
								{versions.map((version) => (
									<li
										key={`${version.relpath}:${version.sha256}`}
										className={`rounded-md border p-3 ${
											version.status === "current"
												? "border-primary-200 bg-primary-50"
												: "border-gray-200 bg-white"
										}`}
									>
										<div className="flex flex-wrap items-center gap-2">
											<StatusChip status={version.status} />
											{version.status === "current" && (
												<span className="text-xs font-medium text-primary-700">
													canonical
												</span>
											)}
											{version.in_body_date && (
												<span className="text-xs text-text-muted">
													in-body date {version.in_body_date}
												</span>
											)}
										</div>
										<div className="mt-1 break-all font-mono text-xs text-text-base">
											{version.relpath}
										</div>
										{version.evidence && (
											<p className="mt-1 text-xs italic text-text-muted">
												“{version.evidence}”
											</p>
										)}
									</li>
								))}
							</ol>
						)}
					</section>
				)}
			</div>
		</aside>
	);
};

export default CabinetNodePanel;
